// components/automations/AutomationsList.js
import React, { useState } from 'react';
import { AutomationCard } from './AutomationCard';
import { Input, Select, Loading } from '../common';
import { HEBREW_TRANSLATIONS } from '../../constants/translations';

export function AutomationsList({ 
  automations = [], 
  loading = false, 
  onEdit, 
  onToggle, 
  onDelete 
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Filter automations based on search and filters
  const filteredAutomations = automations.filter(automation => {
    // Search filter
    const matchesSearch = !searchQuery || 
      automation.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      automation.description.toLowerCase().includes(searchQuery.toLowerCase());

    // Trigger type filter
    const matchesTrigger = !filterTrigger || automation.triggerType === filterTrigger;

    // Status filter
    const matchesStatus = filterStatus === '' || 
      (filterStatus === 'active' && automation.isActive) ||
      (filterStatus === 'inactive' && !automation.isActive);

    return matchesSearch && matchesTrigger && matchesStatus;
  });

  // Loading state
  if (loading && automations.length === 0) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                <div>
                  <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
              <div className="w-12 h-6 bg-gray-200 rounded-full"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (!loading && automations.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{HEBREW_TRANSLATIONS.noAutomationsYet}</h3>
        <p className="text-gray-600 mb-6">
          {HEBREW_TRANSLATIONS.createFirstAutomation}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <Input
            type="text"
            placeholder={HEBREW_TRANSLATIONS.searchAutomations}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          {automations.length === 1 
            ? HEBREW_TRANSLATIONS.showingAutomation.replace('{count}', filteredAutomations.length).replace('{total}', automations.length)
            : HEBREW_TRANSLATIONS.showingAutomations.replace('{count}', filteredAutomations.length).replace('{total}', automations.length)
          }
        </span>
        {(searchQuery || filterTrigger || filterStatus) && (
          <button
            onClick={() => {
              setSearchQuery('');
              setFilterTrigger('');
              setFilterStatus('');
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            {HEBREW_TRANSLATIONS.clearFilters}
          </button>
        )}
      </div>

      {/* Automation Cards */}
      {filteredAutomations.length > 0 ? (
        <div className="space-y-4">
          {filteredAutomations.map(automation => (
            <AutomationCard
              key={automation.id}
              automation={automation}
              onEdit={onEdit}
              onToggle={onToggle}
              onDelete={onDelete}
            />
          ))}
        </div>
      ) : (
        // No results state
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{HEBREW_TRANSLATIONS.noAutomationsFound}</h3>
          <p className="text-gray-600">
            {HEBREW_TRANSLATIONS.adjustSearchCriteria}
          </p>
        </div>
      )}

      {/* Loading overlay for updates */}
      {loading && automations.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-10 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 shadow-lg">
            <Loading size="sm" />
          </div>
        </div>
      )}
    </div>
  );
}